import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Text } from '@column/column-ui-kit';

import { Inner } from '~/styles';

import { TimelineEvent, TimelineEventProps } from './TimelineEvent';

export interface TimelineEventsEntry extends Omit<TimelineEventProps, 'showDate'> {
  showDate?: boolean;
}

export interface TimelineEventsProps {
  entries: TimelineEventsEntry[];
  title?: string | boolean;
}

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

const StyledText = styled(Text)`
  margin-bottom: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    margin-bottom: 24px;
  }
`;

export const TimelineEvents: React.FC<TimelineEventsProps> = ({ entries, title = 'Timeline' }) => {
  const entriesWithDates = useMemo(() => {
    return entries.filter((entry) => entry.date);
  }, [entries]);

  return (
    <TimelineWrapper>
      <Inner px="var(--page-margin)">
        {title !== false && typeof title === 'string' && (
          <StyledText as="h3" size={16} color="foreground" weight="medium">
            {title}
          </StyledText>
        )}
        {entries.map((entry, idx) => {
          const isFirstWithDate = Boolean(entry.date && entry === entriesWithDates[0]);
          const isLastWithDate = Boolean(entry.date && entry === entriesWithDates[entriesWithDates.length - 1]);
          const showDate = entry.showDate !== undefined ? entry.showDate : isFirstWithDate || isLastWithDate;

          return (
            <TimelineEvent
              key={`event-${idx}`}
              label={entry.label}
              date={entry.date}
              status={entry.status}
              icon={entry.icon}
              iconColor={entry.iconColor}
              showDate={showDate}
              link={entry.link}
              content={entry.content}
              additionalContent={entry.additionalContent}
            />
          );
        })}
      </Inner>
    </TimelineWrapper>
  );
};
